import { createStyles } from 'antd-style'
import { toNumber } from 'lodash'

import { ListingItem, ListingQuery } from '@eastcoal/api/trade/interface'
import { queryMallListingPageApi } from '@eastcoal/api/trade/listingMall/queryMallListingPage'

import { NavigateButton } from '@eastcoal/common/components/Button/NavigateButton'
import { RemoteTable } from '@eastcoal/common/components/RemoteTable'
import { RemoteTableProps } from '@eastcoal/common/components/RemoteTable/interface'
import { pretty } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'
import { stringifyUrl } from '@eastcoal/common/utils/querystring'
import { gotoLogin } from '@eastcoal/common/utils/route'

import { useAuthStore } from '@eastcoal/auth/hooks/useStore'
import { EaListingIcon } from '@eastcoal/icon/EaListingIcon'
import { useLocation, useNavigate } from '@eastcoal/router/exports/react-router'
import { ListingStatus } from '@eastcoal/shared/enums/ListingStatus'

import { FloorBlock } from '../../../components/FloorBlock'
import {
  LISTING_DETAIL_PAGE_PATH,
  LISTING_LIST_PAGE_PATH
} from '../../Listing/constants/path'
import { QuotaPopover } from '../../Listing/List/components/QuotaPopover'
import { diffFromNow } from '../../Listing/utils/diffFromNow'

import { ListingFloorTableProps } from './interface'

const useStyles = createStyles({
  row: {
    cursor: 'pointer'
  }
})

const columns: ListingFloorTableProps['columns'] = [
  {
    title: '品种',
    dataIndex: 'kindName',
    align: 'center'
  },
  {
    title: '产品属性',
    dataIndex: 'listingItemList',
    align: 'center',
    onCell: () => ({
      onClick: e => {
        e.stopPropagation()
      }
    }),
    render: value => <QuotaPopover quotas={value} />
  },
  {
    title: '产地',
    dataIndex: 'producerName',
    align: 'center'
  },
  {
    title: '交货地',
    dataIndex: 'deliveryPlaceName',
    align: 'center'
  },
  {
    title: '挂牌量(吨)',
    dataIndex: 'saleableWeight',
    valueType: 'digit',
    align: 'right'
  },
  {
    title: '剩余库存(吨)',
    dataIndex: 'remainingWeight',
    valueType: 'digit',
    align: 'right'
  },
  {
    title: '剩余时间',
    dataIndex: 'validDate',
    align: 'center',
    render: value => {
      return diffFromNow(value)
    }
  },
  {
    title: '价格',
    dataIndex: 'listingPrice',
    render: (_, record) => {
      const { listingPrice, priceUnit } = record
      const hiddenPrice = !toNumber(listingPrice ?? 0)
      return (
        <span style={{ color: 'red' }}>
          {hiddenPrice ? '电议' : pretty(listingPrice)}
          {!hiddenPrice && ` ${priceUnit}`}
        </span>
      )
    },
    align: 'end'
  },
  {
    title: '供应商',
    dataIndex: 'memberName',
    width: 240,
    ellipsis: true
  }
]

export const ListingFloor = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { logined } = useAuthStore(state => pick(state, ['logined']))
  const onRow: RemoteTableProps<
    ListingItem,
    ListingQuery
  >['onRow'] = record => {
    return {
      onClick: () => {
        if (logined) {
          navigate(
            stringifyUrl({
              url: LISTING_DETAIL_PAGE_PATH,
              query: {
                id: record.id
              }
            })
          )
        } else {
          gotoLogin()
        }
      }
    }
  }

  const { styles } = useStyles()

  return (
    <FloorBlock
      icon={<EaListingIcon />}
      title='挂牌交易'
      extra={
        <NavigateButton newtab to={LISTING_LIST_PAGE_PATH} type='link'>
          更多&gt;
        </NavigateButton>
      }
    >
      <RemoteTable
        api={queryMallListingPageApi}
        bordered={false}
        columns={columns}
        defaultCurrent={1}
        defaultPageSize={10}
        pagination={false}
        rowClassName={styles.row}
        rowKey='id'
        requestParams={{
          kindCodeList: [state?.kindNode?.code],
          listingStatus: [ListingStatus.SUCCESS],
          orders: [
            {
              column: 'listing_time',
              asc: false
            }
          ]
        }}
        onRow={onRow}
      />
    </FloorBlock>
  )
}
